import { useCallback, useEffect, useMemo, useState } from "react";
import { Archive, PlusCircle, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/Button";
import { cn } from "@/utils/cn";
import { useAuth } from "@/hooks/useAuth";
import { useSolicitacaoPagamentoAccess } from "@/hooks/useSolicitacaoPagamentoAccess";
import { listSolicitacoesPagamento, type SolicitacaoPagamentoDto } from "@/services/solicitacaoPagamento";
import { SolicitacaoPagamentoWizard } from "./SolicitacaoPagamentoWizard";
import { SolicitacaoPagamentoApprovalFlow } from "./SolicitacaoPagamentoApprovalFlow";
import { SolicitacaoPagamentoDashboardFilterBar } from "./SolicitacaoPagamentoDashboardFilterBar";
import { SolicitacaoPagamentoTable } from "./SolicitacaoPagamentoTable";
import { SolicitacaoPagamentoArquivadasModal } from "./SolicitacaoPagamentoArquivadasModal";
import {
  EMPTY_DASHBOARD_FILTERS,
  buildDashboardFilterOptions,
  filterDashboardSolicitacoes,
  type DashboardFilterValues,
} from "./dashboardFilters";
import {
  countByStage,
  filterMinhasSolicitacoes,
  mergeUniqueSolicitacoes,
  type ApprovalStageId,
} from "./stageUtils";

type ListTab = "todas" | "minhas";
type ViewMode = "dashboard" | "criar";

export function SolicitacaoPagamentoPage() {
  const { user } = useAuth();
  const { canAccessModule, hasFullAccess, canAprovarFinanceira, isAdmin, isMaster } =
    useSolicitacaoPagamentoAccess();
  const userId = user?.id ?? "";
  const canViewAllSolicitacoes = isAdmin || isMaster || hasFullAccess;

  const [viewMode, setViewMode] = useState<ViewMode>("dashboard");
  const [listTab, setListTab] = useState<ListTab>(canViewAllSolicitacoes ? "todas" : "minhas");
  const [manageAll, setManageAll] = useState(false);
  const [minhas, setMinhas] = useState<SolicitacaoPagamentoDto[]>([]);
  const [pendentesOperacional, setPendentesOperacional] = useState<SolicitacaoPagamentoDto[]>([]);
  const [pendentesFinanceira, setPendentesFinanceira] = useState<SolicitacaoPagamentoDto[]>([]);
  const [aguardandoSienge, setAguardandoSienge] = useState<SolicitacaoPagamentoDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editingItem, setEditingItem] = useState<SolicitacaoPagamentoDto | null>(null);
  const [duplicateFrom, setDuplicateFrom] = useState<SolicitacaoPagamentoDto | null>(null);
  const [filterValues, setFilterValues] = useState<DashboardFilterValues>(EMPTY_DASHBOARD_FILTERS);
  const [arquivadasOpen, setArquivadasOpen] = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await listSolicitacoesPagamento();
      setManageAll(data.manageAll);
      setMinhas(data.minhas);
      setPendentesOperacional(data.pendentesOperacional);
      setPendentesFinanceira(data.pendentesFinanceira);
      setAguardandoSienge(data.aguardandoSienge);
    } catch (e: unknown) {
      const msg =
        (e as { response?: { data?: { error?: string } } })?.response?.data?.error ??
        "Erro ao carregar solicitações.";
      setError(msg);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  useEffect(() => {
    if (!canViewAllSolicitacoes && listTab === "todas") {
      setListTab("minhas");
    }
  }, [canViewAllSolicitacoes, listTab]);

  const allItems = useMemo(
    () =>
      mergeUniqueSolicitacoes(
        minhas,
        pendentesOperacional,
        pendentesFinanceira,
        aguardandoSienge,
      ),
    [minhas, pendentesOperacional, pendentesFinanceira, aguardandoSienge],
  );

  const tabItems = useMemo(() => {
    if (listTab === "todas" && canViewAllSolicitacoes) {
      return allItems;
    }
    return filterMinhasSolicitacoes(allItems, userId, {
      manageAll: manageAll || canViewAllSolicitacoes,
      canAprovarFinanceira,
    });
  }, [allItems, listTab, userId, manageAll, canViewAllSolicitacoes, canAprovarFinanceira]);

  const filteredItems = useMemo(
    () => filterDashboardSolicitacoes(tabItems, filterValues),
    [tabItems, filterValues],
  );

  const stageCounts = useMemo(() => countByStage(tabItems), [tabItems]);

  const filterOptions = useMemo(
    () => buildDashboardFilterOptions(tabItems),
    [tabItems],
  );

  const handleSubmitted = useCallback(() => {
    setEditingItem(null);
    setDuplicateFrom(null);
    refresh();
    setViewMode("dashboard");
  }, [refresh]);

  const handleEdit = useCallback((item: SolicitacaoPagamentoDto) => {
    setDuplicateFrom(null);
    setEditingItem(item);
    setViewMode("criar");
  }, []);

  const handleDuplicate = useCallback((item: SolicitacaoPagamentoDto) => {
    setEditingItem(null);
    setDuplicateFrom(item);
    setViewMode("criar");
  }, []);

  const handleStageClick = useCallback((stage: ApprovalStageId) => {
    setFilterValues((prev) => ({
      ...prev,
      etapaFilter: prev.etapaFilter === stage ? "" : stage,
    }));
  }, []);

  if (!canAccessModule) {
    return (
      <div className="flex flex-col gap-6">
        <h1 className="text-xl font-bold text-slate-800">Solicitações de Pagamento</h1>
        <p className="text-sm text-slate-500">
          Seu perfil não possui permissão para acessar Solicitação de Pagamento.
        </p>
      </div>
    );
  }

  if (viewMode === "criar") {
    return (
      <div className="flex flex-col gap-6">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div>
            <p className="text-xs text-slate-400">Solicitações de Pagamento</p>
            <h1 className="text-xl font-bold text-slate-800 mt-0.5">
              {editingItem
                ? "Editar solicitação"
                : duplicateFrom
                  ? "Nova solicitação (cópia)"
                  : "Nova solicitação"}
            </h1>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              setEditingItem(null);
              setDuplicateFrom(null);
              setViewMode("dashboard");
            }}
          >
            Voltar ao painel
          </Button>
        </div>

        {editingItem ? (
          <SolicitacaoPagamentoWizard
            editItem={editingItem}
            onSubmitted={handleSubmitted}
            onCancelEdit={() => {
              setEditingItem(null);
              setDuplicateFrom(null);
              setViewMode("dashboard");
            }}
          />
        ) : (
          <SolicitacaoPagamentoWizard
            duplicateFrom={duplicateFrom}
            onSubmitted={handleSubmitted}
          />
        )}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-5">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <p className="text-xs text-slate-400">Solicitações de Pagamento</p>
          <h1 className="text-xl md:text-2xl font-bold text-slate-800 mt-0.5">
            Acompanhe o status das suas solicitações
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Visualize em que etapa cada solicitação está no processo de aprovação
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button
            variant="secondary"
            size="sm"
            leftIcon={<Archive className="w-4 h-4" />}
            onClick={() => setArquivadasOpen(true)}
          >
            Arquivadas
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={refresh}
            loading={loading}
            leftIcon={<RefreshCw className="w-4 h-4" />}
          >
            Atualizar
          </Button>
          <Button
            variant="primary"
            size="sm"
            leftIcon={<PlusCircle className="w-4 h-4" />}
            onClick={() => {
              setEditingItem(null);
              setDuplicateFrom(null);
              setViewMode("criar");
            }}
          >
            Nova solicitação
          </Button>
        </div>
      </div>

      {error && (
        <p className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2">
          {error}
        </p>
      )}

      {/* Fluxo de aprovação */}
      <SolicitacaoPagamentoApprovalFlow
        counts={stageCounts}
        activeStage={filterValues.etapaFilter}
        onStageClick={handleStageClick}
      />

      {/* Lista com abas, filtros e tabela */}
      <div className="rounded-xl border border-slate-200 bg-white shadow-card overflow-hidden">
        {/* Tabs */}
        <div className="border-b border-slate-200 px-5">
          <nav className="flex gap-6" aria-label="Abas de solicitações">
            {(
              [
                ...(canViewAllSolicitacoes
                  ? [{ id: "todas" as const, label: "Todas as solicitações" }]
                  : []),
                { id: "minhas" as const, label: "Minhas solicitações" },
              ] as const
            ).map((tab) => {
              const active = listTab === tab.id;
              return (
                <button
                  key={tab.id}
                  type="button"
                  onClick={() => setListTab(tab.id)}
                  className={cn(
                    "py-3.5 text-sm font-medium border-b-2 transition-colors -mb-px",
                    active
                      ? "border-brand-600 text-brand-700"
                      : "border-transparent text-slate-500 hover:text-slate-700",
                  )}
                  aria-selected={active}
                  role="tab"
                >
                  {tab.label}
                </button>
              );
            })}
          </nav>
        </div>

        {/* Filtros */}
        <div className="px-5 py-4 border-b border-slate-100">
          <SolicitacaoPagamentoDashboardFilterBar
            values={filterValues}
            onChange={setFilterValues}
            fornecedorOptions={filterOptions.fornecedorOptions}
          />
        </div>

        {/* Tabela */}
        <div className="px-5 py-2 border-b border-slate-100 bg-slate-50/60">
          <p className="text-xs text-slate-500">
            {loading
              ? "Carregando..."
              : filteredItems.length === tabItems.length
                ? `${filteredItems.length} solicitação${filteredItems.length === 1 ? "" : "ões"}`
                : `${filteredItems.length} de ${tabItems.length} solicitações`}
            {!loading && tabItems.length > 0 && (() => {
              const dates = tabItems.map((i) => i.createdAt.slice(0, 10)).sort();
              return (
              <span className="text-slate-400">
                {" "}
                · período {dates[0]} a {dates[dates.length - 1]}
              </span>
              );
            })()}
          </p>
        </div>
        <SolicitacaoPagamentoTable
          items={filteredItems}
          loading={loading}
          emptyMessage={
            filterValues.etapaFilter ||
            filterValues.search ||
            filterValues.fornecedorFilter ||
            filterValues.dateFrom ||
            filterValues.dateTo
              ? "Nenhuma solicitação encontrada com os filtros aplicados."
              : listTab === "minhas"
                ? "Nenhuma solicitação criada por você ou aguardando sua aprovação."
                : "Nenhuma solicitação encontrada."
          }
          onRefresh={refresh}
          onEdit={handleEdit}
          onDuplicate={handleDuplicate}
          canFinalizarSienge={hasFullAccess || canAprovarFinanceira}
        />
      </div>

      <SolicitacaoPagamentoArquivadasModal
        open={arquivadasOpen}
        onClose={() => setArquivadasOpen(false)}
      />
    </div>
  );
}
